/**
 * The plats down the left of the Tables tab, with a search over plats and
 * houses. A list rather than a tab per plat: a portfolio of twenty plats is
 * twenty rows here and still fits, and "All plats" compares them side by side.
 */

import { Search } from "lucide-react";
import React from "react";

export interface PlatListEntry {
	platId: string;
	platName: string;
	houses: number;
}

export interface PlatListProps {
	plats: readonly PlatListEntry[];
	totalHouses: number;
	/** The plat open, or null for All plats. */
	selected: string | null;
	onSelect: (platId: string | null) => void;
	query: string;
	onQueryChange: (query: string) => void;
}

const ROW =
	"flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]";

const PlatList: React.FC<PlatListProps> = ({
	plats,
	totalHouses,
	selected,
	onSelect,
	query,
	onQueryChange,
}) => {
	const needle = query.trim().toLowerCase();
	const shown = needle
		? plats.filter((plat) => plat.platName.toLowerCase().includes(needle))
		: plats;
	const entry = (id: string | null, label: string, count: number) => {
		const active = selected === id;
		return (
			<li key={id ?? "all"}>
				<button
					type="button"
					onClick={() => onSelect(id)}
					aria-current={active ? "true" : undefined}
					className={`${ROW} ${
						active
							? "bg-[#E6F2F1] font-semibold text-[#004C4B]"
							: "text-[#1F2937] hover:bg-gray-50"
					}`}
				>
					<span className="truncate">{label}</span>
					<span className="ml-2 text-xs tabular-nums text-[#6B7280]">{count}</span>
				</button>
			</li>
		);
	};
	return (
		<nav aria-label="Plats" className="flex w-full flex-col gap-2 lg:w-56 lg:flex-shrink-0">
			<label className="relative block">
				<span className="sr-only">Find a plat or house</span>
				<Search
					className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9a9ea3]"
					aria-hidden
				/>
				<input
					type="search"
					value={query}
					onChange={(event) => onQueryChange(event.target.value)}
					placeholder="Find a plat or house"
					className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2.5 text-sm text-[#1F2937] placeholder:text-[#9a9ea3] focus:border-[#008782] focus:outline-none"
				/>
			</label>
			<ul className="flex flex-col gap-0.5">
				{entry(null, "All plats", totalHouses)}
				{shown.map((plat) => entry(plat.platId, plat.platName, plat.houses))}
			</ul>
		</nav>
	);
};

export default PlatList;
